import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { branchesApi, branchesWriteApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

interface FormState {
  id?: string; code: string; name: string; gstin: string; isActive: boolean;
}
const blank = (): FormState => ({ code: "", name: "", gstin: "", isActive: true });

/**
 * UC admin.branch.* — Branches CRUD. Soft-delete refuses when warehouses or documents
 * reference the branch (server enforces); UI surfaces the conflict.
 */
export function BranchesPage() {
  const qc = useQueryClient();
  const canCreate = useAuth(s => s.hasPermission("admin.branch.create"));
  const canEdit   = useAuth(s => s.hasPermission("admin.branch.edit"));
  const canDelete = useAuth(s => s.hasPermission("admin.branch.delete"));

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(blank());
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["branches-list"],
    queryFn: () => branchesApi.list(),
  });

  const startCreate = () => { setForm(blank()); setError(null); setOpen(true); };
  const startEdit = async (id: string) => {
    const b = await branchesWriteApi.get(id);
    setForm({ id: b.id, code: b.code, name: b.name, gstin: b.gstin ?? "", isActive: b.isActive });
    setError(null); setOpen(true);
  };

  const saveMutation = useMutation({
    meta: { successMessage: "Branch saved" },
    mutationFn: async () => {
      if (form.id) {
        await branchesWriteApi.update(form.id, {
          id: form.id, code: form.code, name: form.name,
          gstin: form.gstin || undefined, isActive: form.isActive,
        });
      } else {
        await branchesWriteApi.create({
          code: form.code, name: form.name, gstin: form.gstin || undefined,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["branches-list"] });
      qc.invalidateQueries({ queryKey: ["branches"] });
      setOpen(false);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => branchesWriteApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["branches-list"] }),
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Branches</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate}>New branch</Button>
        )}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Paper sx={{ p: 2 }}>
        {query.isLoading ? <CircularProgress /> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data?.map(b => (
                <TableRow key={b.id} hover>
                  <TableCell><b>{b.code}</b></TableCell>
                  <TableCell>{b.name}</TableCell>
                  <TableCell>
                    <Chip size="small" color={b.isActive ? "success" : "default"}
                      label={b.isActive ? "Active" : "Inactive"} />
                  </TableCell>
                  <TableCell align="right">
                    {canEdit && (
                      <IconButton size="small" onClick={() => startEdit(b.id)}><EditIcon fontSize="small" /></IconButton>
                    )}
                    {canDelete && (
                      <IconButton size="small" color="error"
                        onClick={() => { if (confirm(`Delete branch ${b.code}?`)) deleteMutation.mutate(b.id); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? `Edit branch ${form.code}` : "New branch"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Code *" value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))} />
            <TextField label="Name *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
            <TextField label="GSTIN (15 chars)" value={form.gstin}
              onChange={(e) => setForm(f => ({ ...f, gstin: e.target.value }))} inputProps={{ maxLength: 15 }} />
            {form.id && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch checked={form.isActive} onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                <Typography>{form.isActive ? "Active" : "Inactive"}</Typography>
              </Stack>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {(!form.code || !form.name) && (
              <Alert severity="info">{!form.code ? "Code is required." : "Name is required."}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained"
            disabled={!form.code || !form.name || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}>
            {form.id ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
